// ============================================================================
// Public magic-link landing page for advisor-proposed calls.
// Route: /investor/call-requests/by-token/:token
// No auth required — the token is the auth.
//
// Flow:
//   1. GET /api/investor/call-proposals/by-token/:token — peek (no consume)
//   2. User clicks Accept (slot N) | Suggest other times | Decline
//   3. POST consumes the matching purpose token, server cancels tentative
//      Google event + mints real Meet (on accept) or notifies banker (counter
//      / decline).
// ============================================================================
import { useEffect, useState } from "react";
import { useRoute } from "wouter";
import { CalendarClock, CheckCircle2, Loader2, X, Phone } from "lucide-react";

const ORANGE = "#FF5C00";
const BASE = import.meta.env.BASE_URL;

interface ProposalDetail {
  token: { purpose: string; payload: any; expired: boolean; used: boolean; expiresAt: string };
  request: {
    id: number;
    status: string;
    direction: string;
    proposedSlots: Array<{ startUtc: string; endUtc: string }>;
    counterProposedSlots: any;
    reason: string | null;
    expiresAt: string | null;
  };
  banker: { name: string; email: string } | null;
  investor: { name: string } | null;
  company: { id: number; name: string } | null;
}

function fmtIst(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  }) + " IST";
}

export default function CallRequestAccept() {
  const [, params] = useRoute<{ token: string }>("/investor/call-requests/by-token/:token");
  const token = params?.token || "";
  const [data, setData] = useState<ProposalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ kind: "accepted" | "countered" | "declined"; meetUrl?: string | null; startUtc?: string } | null>(null);
  // Counter-propose form
  const [counterMode, setCounterMode] = useState(false);
  const [counterSlots, setCounterSlots] = useState<string[]>([""]);
  const [counterDuration, setCounterDuration] = useState(60);
  // Decline form
  const [declineMode, setDeclineMode] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${BASE}api/investor/call-proposals/by-token/${token}`);
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          setError(e.error || "Could not load this proposal.");
        } else {
          setData(await r.json());
        }
      } catch { setError("Network error."); }
      setLoading(false);
    })();
  }, [token]);

  const accept = async (slotIndex: number) => {
    setSubmitting(true); setError(null);
    try {
      const r = await fetch(`${BASE}api/investor/call-proposals/by-token/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slotIndex }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setError(j.error || "Could not accept."); }
      else { setSuccess({ kind: "accepted", meetUrl: j.meetUrl, startUtc: j.scheduledStartUtc }); }
    } catch { setError("Network error."); }
    setSubmitting(false);
  };

  const submitCounter = async () => {
    setSubmitting(true); setError(null);
    try {
      const cleaned = counterSlots.map(s => s.trim()).filter(Boolean);
      if (cleaned.length < 1) { setError("Pick at least one time."); setSubmitting(false); return; }
      const slots = cleaned.map(s => {
        const start = new Date(s);
        if (isNaN(start.getTime())) throw new Error("Invalid time");
        return { startUtc: start.toISOString(), endUtc: new Date(start.getTime() + counterDuration * 60_000).toISOString() };
      });
      const r = await fetch(`${BASE}api/investor/call-proposals/by-token/${token}/counter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slots }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setError(j.error || "Could not send counter."); }
      else { setSuccess({ kind: "countered" }); }
    } catch (e: any) { setError(e?.message || "Network error."); }
    setSubmitting(false);
  };

  const submitDecline = async () => {
    setSubmitting(true); setError(null);
    try {
      const r = await fetch(`${BASE}api/investor/call-proposals/by-token/${token}/decline`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason: declineReason.trim() || undefined }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setError(j.error || "Could not decline."); }
      else { setSuccess({ kind: "declined" }); }
    } catch { setError("Network error."); }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
    </div>
  );

  if (error && !data) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-[#141414] border border-white/10 rounded-2xl p-6 text-center">
        <X className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <h1 className="text-white text-lg font-semibold mb-2">This link can't be used</h1>
        <p className="text-white/60 text-sm">{error}</p>
        <p className="text-white/40 text-xs mt-4">If you've already responded, no action is needed. Reply to the original email if you need to change anything.</p>
      </div>
    </div>
  );

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#141414] border border-orange-500/30 rounded-2xl p-6 text-center">
          <CheckCircle2 className="w-12 h-12 text-orange-400 mx-auto mb-3" />
          {success.kind === "accepted" && (
            <>
              <h1 className="text-white text-lg font-semibold mb-2">Call confirmed</h1>
              <p className="text-white/70 text-sm">{success.startUtc && fmtIst(success.startUtc)}</p>
              {success.meetUrl && (
                <a href={success.meetUrl} className="inline-block mt-4 px-5 py-2.5 rounded-lg text-sm font-medium" style={{ background: ORANGE, color: "#fff" }}>Open Google Meet</a>
              )}
              <p className="text-white/40 text-xs mt-4">A Google Calendar invite is on its way.</p>
            </>
          )}
          {success.kind === "countered" && (
            <>
              <h1 className="text-white text-lg font-semibold mb-2">Counter-proposal sent</h1>
              <p className="text-white/70 text-sm">{data?.banker?.name || "The advisor"} will get back to you shortly.</p>
            </>
          )}
          {success.kind === "declined" && (
            <>
              <h1 className="text-white text-lg font-semibold mb-2">Proposal declined</h1>
              <p className="text-white/70 text-sm">No problem — {data?.banker?.name || "the advisor"} has been notified.</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!data) return null;
  const t = data.token;
  const r = data.request;

  // Resolved-elsewhere fence: token still alive but the request itself was
  // already accepted/declined/countered/expired.
  if (t.expired || t.used || r.status !== "pending") {
    let line = "This link has already been used.";
    if (t.expired) line = "This link has expired.";
    else if (r.status === "accepted") line = "You've already accepted this proposal.";
    else if (r.status === "declined") line = "This proposal was declined.";
    else if (r.status === "counter_proposed") line = `${data.banker?.name || "The advisor"} is reviewing your counter-proposal.`;
    else if (r.status === "expired") line = "This proposal expired.";
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#141414] border border-white/10 rounded-2xl p-6 text-center">
          <CalendarClock className="w-10 h-10 text-white/40 mx-auto mb-3" />
          <h1 className="text-white text-lg font-semibold mb-2">No action needed</h1>
          <p className="text-white/60 text-sm">{line}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4">
      <div className="max-w-xl mx-auto">
        <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden">
          <div className="bg-black p-6 text-center border-b border-white/10">
            <div className="inline-block w-10 h-10 rounded-lg leading-[40px] text-white font-bold text-base font-serif" style={{ background: ORANGE }}>F</div>
            <h1 className="text-white text-xl font-serif mt-3">{data.banker?.name || "An advisor"} would like to set up a call</h1>
            <p className="text-white/40 text-[10px] tracking-[0.2em] uppercase font-mono mt-1">Favcy Select</p>
          </div>

          <div className="p-6 space-y-5">
            {data.investor && <p className="text-white/80 text-sm">Hi {data.investor.name.split(" ")[0]},</p>}
            {data.company && <p className="text-white/60 text-xs">Re: <strong className="text-white/80">{data.company.name}</strong></p>}
            {r.reason && (
              <div className="bg-white/[0.04] border border-white/10 rounded-lg p-4">
                <p className="text-white/40 text-[10px] uppercase tracking-wider font-mono mb-1">Agenda</p>
                <p className="text-white/80 text-sm leading-relaxed">{r.reason}</p>
              </div>
            )}

            {!counterMode && !declineMode && (
              <>
                <div>
                  <p className="text-white/40 text-[10px] uppercase tracking-wider font-mono mb-2">Pick one to confirm</p>
                  <div className="space-y-2">
                    {r.proposedSlots.map((s, i) => {
                      const dur = Math.round((new Date(s.endUtc).getTime() - new Date(s.startUtc).getTime()) / 60_000);
                      return (
                        <button
                          key={i}
                          type="button"
                          disabled={submitting}
                          onClick={() => accept(i)}
                          className="w-full flex items-center justify-between p-4 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-orange-500/10 hover:border-orange-400/40 transition-colors text-left disabled:opacity-50"
                        >
                          <div>
                            <p className="text-white text-sm font-medium">{fmtIst(s.startUtc)}</p>
                            <p className="text-white/50 text-xs">{dur} minutes</p>
                          </div>
                          <span className="px-3 py-1.5 rounded-md text-xs font-semibold" style={{ background: ORANGE, color: "#fff" }}>Accept</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={() => setCounterMode(true)} disabled={submitting}
                    className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border border-orange-400/30 text-orange-200 hover:bg-orange-500/10 disabled:opacity-50">
                    Suggest other times
                  </button>
                  <button type="button" onClick={() => setDeclineMode(true)} disabled={submitting}
                    className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border border-white/15 text-white/60 hover:bg-white/[0.04] disabled:opacity-50">
                    Decline
                  </button>
                </div>
              </>
            )}

            {counterMode && (
              <div className="space-y-3">
                <p className="text-white/70 text-sm">Suggest 1-3 times that work better:</p>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-white/40 font-mono">Slot duration</label>
                  <select value={counterDuration} onChange={e => setCounterDuration(parseInt(e.target.value, 10))}
                    className="mt-1 w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white">
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>60 minutes</option>
                    <option value={90}>90 minutes</option>
                  </select>
                </div>
                <div className="space-y-2">
                  {counterSlots.map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-white/40 text-xs font-mono w-4">{i + 1}.</span>
                      <input type="datetime-local" value={s}
                        onChange={e => { const next = [...counterSlots]; next[i] = e.target.value; setCounterSlots(next); }}
                        className="flex-1 bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white" />
                      {counterSlots.length > 1 && (
                        <button type="button" onClick={() => setCounterSlots(counterSlots.filter((_, idx) => idx !== i))} className="text-white/40 hover:text-red-300 p-1">×</button>
                      )}
                    </div>
                  ))}
                </div>
                {counterSlots.length < 3 && (
                  <button type="button" onClick={() => setCounterSlots([...counterSlots, ""])} className="text-orange-300 hover:text-orange-200 text-xs font-medium">+ Add another</button>
                )}
                {error && <p className="text-red-400 text-xs">{error}</p>}
                <div className="flex gap-2 pt-2">
                  <button onClick={() => { setCounterMode(false); setError(null); }} disabled={submitting} className="px-4 py-2 rounded-lg text-sm text-white/60 hover:bg-white/[0.04]">Back</button>
                  <button onClick={submitCounter} disabled={submitting || counterSlots.filter(s => s.trim()).length < 1}
                    className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2" style={{ background: ORANGE, color: "#fff" }}>
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending</> : "Send counter-proposal"}
                  </button>
                </div>
              </div>
            )}

            {declineMode && (
              <div className="space-y-3">
                <p className="text-white/70 text-sm">Tell {data.banker?.name || "them"} why (optional):</p>
                <textarea value={declineReason} onChange={e => setDeclineReason(e.target.value.slice(0, 1000))} rows={3}
                  placeholder="e.g. Not a fit right now / try again next quarter"
                  className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 resize-none" />
                {error && <p className="text-red-400 text-xs">{error}</p>}
                <div className="flex gap-2 pt-2">
                  <button onClick={() => { setDeclineMode(false); setError(null); }} disabled={submitting} className="px-4 py-2 rounded-lg text-sm text-white/60 hover:bg-white/[0.04]">Back</button>
                  <button onClick={submitDecline} disabled={submitting} className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold border border-white/15 text-white/80 hover:bg-white/[0.04] disabled:opacity-50 flex items-center justify-center gap-2">
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending</> : "Decline"}
                  </button>
                </div>
              </div>
            )}

            {error && !counterMode && !declineMode && <p className="text-red-400 text-xs">{error}</p>}
          </div>

          <div className="bg-black/40 px-6 py-3 text-center border-t border-white/5">
            <p className="text-white/30 text-[10px] flex items-center justify-center gap-1.5"><Phone className="w-3 h-3" /> Need help? Reply to the email and Kavya will jump in.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
